import { useState } from 'react'
import { useRoundTables } from '../hooks/useRoundTables'
import { RoundTableCard } from '../components/round-tables/RoundTableCard'
import { RoundTableDetail } from '../components/round-tables/RoundTableDetail'
import { RoundTableCompose } from '../components/round-tables/RoundTableCompose'
import type { RoundTable, RoundTableStatus } from '../types/round-tables'

type FilterTab = 'all' | RoundTableStatus

const TABS: { key: FilterTab; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'open', label: 'Active' },
  { key: 'resolved', label: 'Resolved' },
  { key: 'expired', label: 'Expired' },
]

export function RoundTables() {
  const [activeTab, setActiveTab] = useState<FilterTab>('all')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [showCompose, setShowCompose] = useState(false)

  const { data: roundTables = [], isLoading, error, refetch } = useRoundTables()

  // Detail view
  if (selectedId) {
    return (
      <RoundTableDetail
        rtId={selectedId}
        onBack={() => setSelectedId(null)}
      />
    )
  }

  // Filter
  const filtered =
    activeTab === 'all'
      ? roundTables
      : roundTables.filter((rt) => rt.status === activeTab)

  // Sort: open first, then by created_at descending
  const sorted = [...filtered].sort((a, b) => {
    if (a.status === 'open' && b.status !== 'open') return -1
    if (b.status === 'open' && a.status !== 'open') return 1
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  })

  // Counts per tab
  const counts = { all: roundTables.length, open: 0, resolved: 0, expired: 0 }
  for (const rt of roundTables) {
    if (rt.status in counts) counts[rt.status as RoundTableStatus]++
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-swiss-body text-swiss-gray-500">Loading round tables...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <div className="text-swiss-body text-swiss-red mb-4">
          {error instanceof Error ? error.message : 'Failed to load round tables'}
        </div>
        <button
          onClick={() => refetch()}
          className="text-swiss-small text-swiss-black underline underline-offset-2 cursor-pointer"
        >
          Retry
        </button>
      </div>
    )
  }

  return (
    <div>
      {/* Page Header */}
      <div className="mb-8 flex items-start justify-between">
        <div>
          <h1 className="text-[24px] font-semibold tracking-tight leading-[1.2]">Round Tables</h1>
          <p className="text-swiss-gray-500 mt-2 text-[14px]">
            Multi-agent deliberation sessions — review, speak, and resolve
          </p>
        </div>
        <button
          onClick={() => refetch()}
          className="px-3 py-1.5 text-[13px] border border-swiss-gray-200 text-swiss-black bg-transparent cursor-pointer transition-colors duration-150 hover:border-swiss-black"
          aria-label="Refresh"
        >
          Refresh
        </button>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-0 border-b border-swiss-gray-200 mb-6" role="tablist">
        {TABS.map((tab) => {
          const isActive = activeTab === tab.key
          const count = counts[tab.key]
          return (
            <button
              key={tab.key}
              role="tab"
              aria-selected={isActive}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-3 text-[13px] font-medium cursor-pointer transition-colors duration-150 border-b-2 -mb-px ${
                isActive
                  ? 'border-swiss-black text-swiss-black'
                  : 'border-transparent text-swiss-gray-500 hover:text-swiss-black'
              }`}
            >
              {tab.label}
              {count > 0 && (
                <span
                  className={`ml-2 inline-block min-w-[18px] text-center px-1 py-0 text-swiss-caption font-medium ${
                    isActive
                      ? 'bg-swiss-black text-swiss-white'
                      : 'bg-swiss-gray-200 text-swiss-gray-600'
                  }`}
                >
                  {count}
                </span>
              )}
            </button>
          )
        })}
      </div>

      {/* List */}
      <div>
        {sorted.length === 0 ? (
          <div className="bg-swiss-off-white border border-swiss-gray-200 p-12 text-center">
            <div className="text-swiss-body text-swiss-gray-500 mb-2">No round tables</div>
            <div className="text-swiss-small text-swiss-gray-400">
              {activeTab === 'all'
                ? 'No sessions have been convened yet'
                : `No ${activeTab} sessions`}
            </div>
          </div>
        ) : (
          <div>
            {sorted.map((rt) => (
              <RoundTableCard
                key={rt.id}
                rt={rt}
                onClick={(rt: RoundTable) => setSelectedId(rt.id)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Convene FAB */}
      <button
        onClick={() => setShowCompose(true)}
        className="fixed bottom-6 right-6 w-12 h-12 bg-swiss-black text-swiss-white flex items-center justify-center text-swiss-h1 cursor-pointer z-10 hover:bg-swiss-gray-700 transition-colors duration-150"
        aria-label="Convene new round table"
      >
        +
      </button>

      {/* Compose modal */}
      {showCompose && (
        <RoundTableCompose
          onClose={() => setShowCompose(false)}
          onCreated={() => {
            setShowCompose(false)
            refetch()
          }}
        />
      )}
    </div>
  )
}
